import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bug, Broadcast, Buildings, Bank, Play, CheckCircle, XCircle, FileArrowDown, MagicWand, Clock, Sparkle, Trash, CircleNotch, Eye, EyeSlash, Plus, ArrowSquareOut, WarningCircle, SquaresFour, Rows, PencilSimple, Lock } from "@phosphor-icons/react";
import { getCrawlerLogs, getCrawlerSources, getMissingCrawlerBodies, addCrawlerSource, updateCrawlerSource, runCrawler, getCounsellingDocs, approveCounsellingDocs,
  rejectCounsellingDocs, setCounsellingAutoApprove, runSeasonPrimer, runEodSweep, seedDemoSnapshot, clearDemoSnapshot,
  seedDemoNews, clearDemoNews, getCrawlerSchedules, setCrawlerSchedule, parseSchedule, setScheduleVisibility, classifyCounselling, relinkCounselling, setCounsellingDocHint, ingestCounsellingYear, manualDiscoverCounselling, setCounsellingDocType,
  setNmcTableHint, reingestNmcDoc, checkSmFormat, getSignedDocUrl, ingestFeesDoc, getIngestJob, cancelDocIngest, smIngestPlan, ingestSmWithRound } from "../lib/api";
import AgentChat from "../components/AgentChat";

const FREQ_LABEL = { "5m": "5 min", "10m": "10 min", "15m": "15 min", "30m": "30 min", hourly: "Hour", daily: "Day", weekly: "Week", monthly: "Month" };

// Live ingest progress + cancel for a doc that is currently "ingesting".
function IngestProgress({ d, onDone }) {
  const [job, setJob] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const pid = d.progress_job_id;
  useEffect(() => {
    if (!pid) return;
    let alive = true;
    const tick = async () => {
      try { const { data } = await getIngestJob(pid); if (alive) setJob(data); } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [pid]);
  // 1s ticking "elapsed since start" — a diagnostic to see how long it ran before a restart.
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startMs = d.ingest_started_at ? Date.parse(d.ingest_started_at) : null;
  const elapsedS = startMs ? Math.max(0, Math.floor((nowTs - startMs) / 1000)) : null;
  const fmtElapsed = (s) => {
    if (s == null) return null;
    const m = Math.floor(s / 60), sec = s % 60;
    return m > 0 ? `${m}m ${sec}s in` : `${sec}s in`;
  };
  const fmtEta = (s) => {
    if (s == null) return null;
    if (s <= 0) return "finishing…";
    const m = Math.floor(s / 60), sec = s % 60;
    return m > 0 ? `~${m}m ${sec}s left` : `~${sec}s left`;
  };
  const onCancel = async () => {
    setCancelling(true);
    try {
      const r = await cancelDocIngest(d.url);
      toast.success(r?.data?.cleared ? "Reset — you can re-ingest now" : "Cancelling…");
      // Refresh the docs list (immediately for an orphan reset; shortly after for a live cancel).
      onDone && onDone();
      setTimeout(() => onDone && onDone(), 2000);
    } catch { toast.error("Cancel failed"); setCancelling(false); }
  };
  const PHASE = {
    acquiring_lock: "Acquiring lock…",
    waiting_ingest: "Waiting for another ingest…",
    preparing: "Lock acquired · preparing…",
    parsing: "Parsing…",
    matching_new: "Matching new colleges…",
    cancelling: "Cancelling…",
    cancelled: "Cancelled",
  };
  const phase = cancelling ? "cancelling" : job?.phase;
  const matching = !cancelling && (job?.phase === "matching_new" || job?.status === "matching_new");
  const hasPages = !!job?.total_pages;
  const lockHeld = ["preparing", "parsing"].includes(job?.phase);
  const read = job?.records_parsed ?? 0;
  const ing = job?.records_ingested ?? 0;
  const eta = fmtEta(job?.eta_seconds);
  const mDone = job?.match_done ?? 0;
  const mTotal = job?.match_total ?? 0;
  const mPct = mTotal ? Math.round((mDone / mTotal) * 100) : 0;
  // Matching (final leg) → its own label; else while parsing show live counts;
  // before that show the phase (lock/queue state).
  const label = matching
    ? `Matching new colleges ${mDone.toLocaleString()}/${(mTotal || "…").toLocaleString?.() ?? mTotal}`
    : hasPages
    ? `Page ${job.pages_done}/${job.total_pages} · ${read.toLocaleString()} read · ${ing.toLocaleString()} ingested${eta ? ` · ${eta}` : ""}`
    : (PHASE[phase] || "Starting…");
  return (
    <div className="flex flex-col gap-1 items-end text-blue-600" data-testid={`ingesting-spinner-${d.url}`}>
      <div className="flex gap-1.5 items-center flex-wrap justify-end">
        <CircleNotch size={13} weight="bold" className="animate-spin" />
        {lockHeld && (
          <span title="Ingest priority lock held — crawls are waiting"
            className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 px-1 rounded" data-testid={`ingest-lock-badge-${d.url}`}>
            <Lock size={9} weight="fill" /> priority
          </span>
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider" data-testid={`ingest-progress-${d.url}`}>
          {label}
        </span>
        {elapsedS != null && (
          <span className="text-[10px] font-bold text-zinc-400 tabular-nums" data-testid={`ingest-elapsed-${d.url}`}>
            · {fmtElapsed(elapsedS)}
          </span>
        )}
        <button onClick={onCancel} disabled={cancelling} data-testid={`ingest-cancel-${d.url}`}
          className="h-6 px-2 text-[9px] font-black uppercase bg-rose-100 text-rose-600 rounded hover:bg-rose-200 disabled:opacity-40">
          {cancelling ? "Cancelling…" : "Cancel"}
        </button>
        <a href="/admin/logs" target="_blank" rel="noreferrer" title="Watch the live server log relay — see where the ingest is / what stalled or reaped it"
          data-testid={`ingest-logs-link-${d.url}`}
          className="h-6 px-2 text-[9px] font-black uppercase bg-zinc-100 text-zinc-600 rounded hover:bg-zinc-200 inline-flex items-center gap-1">
          Live Logs ↗
        </a>
      </div>
      {(matching || hasPages) ? (
        <div className="w-40 h-1 rounded-full bg-blue-100 overflow-hidden">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${matching ? mPct : (job.progress || 0)}%` }} />
        </div>
      ) : null}
    </div>
  );
}


// Crawl-log timestamp → "10 Jul 13:55:06 IST"
const fmtIST = (ts) => {
  if (!ts) return "";
  let s = ts;
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z"; // naive string → treat as UTC
  try {
    return new Date(s).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }) + " IST";
  } catch (e) { return (ts || "").slice(11, 19); }
};

// Relative "last run" label, e.g. "5m ago" / "2h ago" / "3d ago".
const fmtRel = (ts) => {
  if (!ts) return "";
  let s = ts;
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
  const then = new Date(s).getTime();
  if (isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return "just now";
  const m = Math.floor(sec / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const TABS = [
  { key: "colleges", label: "Colleges", icon: Buildings },
  { key: "counselling", label: "Counselling Bodies", icon: Bank },
  { key: "central", label: "Central Newsroom", icon: Broadcast },
];

const STATUS_COLOR = {
  ok: "text-emerald-600", empty: "text-zinc-400", info: "text-sky-600", error: "text-rose-600",
};

const DOC_STATUS_STYLE = {
  detected: "bg-amber-100 text-amber-700",
  approved: "bg-sky-100 text-sky-700",
  ingesting: "bg-blue-100 text-blue-700",
  ingested: "bg-emerald-100 text-emerald-700",
  rejected: "bg-zinc-100 text-zinc-500 line-through",
  ingest_failed: "bg-rose-100 text-rose-700",
  ignored: "bg-zinc-100 text-zinc-400",
};

const DATA_DOC_TYPES = ["allotment", "full_seat_matrix", "provisional_seat_matrix", "stray_seat_matrix", "nmc_seat_matrix"];
const SM_DOC_TYPES = new Set(["full_seat_matrix", "provisional_seat_matrix", "stray_seat_matrix", "nmc_seat_matrix"]);
const SM_VARIETIES = ["full", "provisional_r1", "provisional_r2", "provisional_r3", "provisional_r4", "provisional_r5", "stray_r1", "stray_r2"];
const SM_VARIETY_LABEL = (t) => t === "full" ? "Full seat matrix" : t.startsWith("provisional_r") ? `Provisional R${t.split("_r")[1]}` : t.startsWith("stray_r") ? `Stray R${t.split("_r")[1]}` : t;

// Map a counselling doc's `short` (parser body) to the body value used by
// the /counselling page's body filter (matches filter-options.counselling_bodies).
const SHORT_TO_COUNSELLING_BODY = {
  AP_Govt: "Andhra_Pradesh",
  AP_Mgmt: "Andhra_Pradesh",
  Telangana_Govt: "Telangana",
  Telangana_Mgmt: "Telangana",
};

// Build a deep-link into /counselling that opens the specific body+year+round the
// doc was ingested into. Used from the Detected Documents "Actions" column so
// admins can jump straight to the ingested data.
//   • Allotment docs → `?round=<round_number>` (highlights the allotment pill).
//   • Seat-matrix docs → `?sm=<derived matrix_type>` (highlights the SM pill).
// The mt derivation mirrors backend/counselling_ops.py::ingest_counselling_document.
const counsellingViewHref = (d) => {
  const body = SHORT_TO_COUNSELLING_BODY[d.short] || d.short;
  if (!body || !d.year) return null;
  const q = new URLSearchParams({ body, year: String(d.year) });
  const dt = d.doc_type;
  const rn = d.round_number;
  if (dt === "full_seat_matrix" || dt === "nmc_seat_matrix") {
    q.set("sm", "full");
  } else if (dt === "provisional_seat_matrix" && rn != null) {
    q.set("sm", `provisional_r${rn}`);
  } else if (dt === "stray_seat_matrix" && rn != null) {
    q.set("sm", rn > 100 ? `stray_r${rn - 100}` : "stray_r1");
  } else if (rn != null) {
    // allotment or unknown → point at the allotment pill
    q.set("round", String(rn));
  }
  return `/counselling?${q.toString()}`;
};
const DOC_TYPE_OPTIONS = ["schedule", "information", "merit_list", "allotment", "full_seat_matrix", "provisional_seat_matrix", "stray_seat_matrix", "nmc_seat_matrix", "fees", "other"];

export default function AdminCrawlers() {
  // Default tab is "Counselling Bodies" — that's where most day-to-day operator work
  // happens (detected docs, ingest actions). Newsroom / Colleges are secondary.
  const [tab, setTab] = useState("counselling");
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [stateInput, setStateInput] = useState("all");
  const [schedules, setSchedules] = useState([]);
  const [freqs, setFreqs] = useState(["5m", "10m", "15m", "30m", "hourly", "daily", "weekly", "monthly"]);
  const lastTs = useRef(null);
  const logBox = useRef(null);

  const loadSchedules = useCallback(async () => {
    try {
      const r = await getCrawlerSchedules();
      setSchedules(r.data.schedules || []);
      if (r.data.frequencies) setFreqs(r.data.frequencies);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  const sched = schedules.find((s) => s.crawler === tab) || { enabled: false, freq: "daily" };
  const saveSched = async (patch) => {
    try { const r = await setCrawlerSchedule({ crawler: tab, ...patch }); setSchedules(r.data.schedules || []); }
    catch (e) { /* noop */ }
  };

  const loadSources = useCallback(async (t) => {
    try {
      const res = await getCrawlerSources(t);
      setSources(res.data.sources || []);
      setSummary(res.data.summary || null);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    setLogs([]); lastTs.current = null; loadSources(tab);
  }, [tab, loadSources]);

  // Live poll of logs
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await getCrawlerLogs(tab, lastTs.current);
        const incoming = res.data.logs || [];
        if (alive && incoming.length) {
          lastTs.current = incoming[incoming.length - 1].ts;
          setLogs((prev) => [...prev, ...incoming].slice(-200));
        }
      } catch (e) { /* noop */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [tab]);

  useEffect(() => {
    if (logBox.current) logBox.current.scrollTop = logBox.current.scrollHeight;
  }, [logs]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await runCrawler(
        tab === "counselling" ? { type: "counselling", year: 2026 }
        : tab === "central" ? { type: "central" }
        : { type: "colleges", state: stateInput || "all", remaining: true });
      setTimeout(() => loadSources(tab), 1500);
    } finally { setTimeout(() => setRunning(false), 1200); }
  };

  // Edit a source's URL / name (counselling + central) when the official site changes.
  const [editingSrc, setEditingSrc] = useState(null); // url being edited
  const [editUrl, setEditUrl] = useState("");
  const [editName, setEditName] = useState("");
  const [savingSrc, setSavingSrc] = useState(false);
  const startEditSource = (s) => { setEditingSrc(s.url); setEditUrl(s.url || ""); setEditName(s.short || ""); };
  const cancelEditSource = () => { setEditingSrc(null); setEditUrl(""); setEditName(""); };
  const saveEditSource = async (s) => {
    if (!editUrl.trim()) { toast.error("URL can't be empty"); return; }
    setSavingSrc(true);
    try {
      const r = await updateCrawlerSource({ old_url: s.url, url: editUrl.trim(), short: editName.trim() });
      if (r.data?.updated) {
        toast.success(r.data.reachable ? `Source updated (reachable · ${r.data.note})` : `Source updated (not reachable: ${r.data.note})`);
        cancelEditSource();
        loadSources(tab);
      } else {
        toast.error(r.data?.error || "Could not update source");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSavingSrc(false); }
  };

  // Manual per-source crawl (counselling). Honors the ingest lock server-side —
  // if an ingestion is running, the job is queued and starts once it finishes.
  const [sourceRunning, setSourceRunning] = useState(() => new Set());
  const crawlSource = async (s) => {
    setSourceRunning((prev) => new Set(prev).add(s.url));
    try {
      const res = await runCrawler({ type: "counselling", year: 2026, source_urls: [s.url] });
      const m = res?.data?.message || "Crawl queued";
      if (res?.data?.ingest_active) toast.warning(m);
      else toast.success(`${s.short}: ${m}`);
      setTimeout(() => loadSources("counselling"), 2500);
    } catch (e) {
      toast.error(`Could not queue crawl for ${s.short}`);
    } finally {
      setTimeout(() => setSourceRunning((prev) => { const n = new Set(prev); n.delete(s.url); return n; }), 3000);
    }
  };

  // Add Source popup (counselling only) — dropdown of predictor bodies missing a crawl source.
  const [showAdd, setShowAdd] = useState(false);
  const [missingBodies, setMissingBodies] = useState([]);
  const [addForm, setAddForm] = useState({ body: "", official_name: "", official_url: "" });
  const [addBusy, setAddBusy] = useState(false);
  const openAddSource = async () => {
    setAddForm({ body: "", official_name: "", official_url: "" });
    setShowAdd(true);
    try {
      const r = await getMissingCrawlerBodies();
      setMissingBodies(r.data.missing_bodies || []);
    } catch (e) { setMissingBodies([]); }
  };
  const submitAddSource = async () => {
    if (!addForm.body || !addForm.official_name.trim() || !addForm.official_url.trim()) {
      toast.error("Pick a body and fill both Official Name and URL.");
      return;
    }
    setAddBusy(true);
    try {
      const r = await addCrawlerSource(addForm);
      if (r.data.error) { toast.error(r.data.error); return; }
      if (r.data.reachable) toast.success(`Added ${addForm.official_name} · URL reachable (${r.data.note})`);
      else toast.warning(`Added ${addForm.official_name}, but URL looks unreachable (${r.data.note}). You can still crawl it later.`);
      setShowAdd(false);
      loadSources("counselling");
    } catch (e) {
      toast.error("Could not add source.");
    } finally { setAddBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-white to-sky-50" data-testid="admin-crawlers-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2 hover:underline" data-testid="crawlers-back-link">
          <ArrowLeft size={12} weight="bold" /> Admin
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <Bug size={26} weight="fill" className="text-[#6043EF]" />
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-[#09090B] font-heading">Crawlers</h1>
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
            <Broadcast size={12} weight="fill" className="animate-pulse" /> Live
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`crawler-tab-${t.key}`}
                className={`h-9 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 rounded-full transition-all ${tab === t.key ? "bg-[#6043EF] text-white shadow-lg shadow-[#6043EF]/30" : "bg-white text-zinc-600 border border-zinc-200 hover:border-[#6043EF]"}`}>
                <Icon size={14} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Auto-crawl schedule (per crawler) */}
        <div className="flex flex-wrap items-center gap-3 mb-5 rounded-xl bg-white border border-zinc-200 px-4 py-2.5 shadow-sm" data-testid="crawler-schedule-control">
          <Clock size={16} weight="fill" className="text-[#6043EF]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Auto-crawl — {TABS.find((t) => t.key === tab)?.label}</span>
          <button onClick={() => saveSched({ enabled: !sched.enabled })} data-testid="schedule-toggle"
            className={`h-8 px-4 text-xs font-black uppercase tracking-wider rounded-full transition-colors ${sched.enabled ? "bg-emerald-500 text-white shadow" : "bg-zinc-100 text-zinc-500"}`}>
            {sched.enabled ? "On" : "Off"}
          </button>
          <span className="text-[11px] text-zinc-400">every</span>
          <select value={sched.freq} onChange={(e) => saveSched({ freq: e.target.value })} data-testid="schedule-freq"
            disabled={!sched.enabled}
            className="h-8 px-2 text-xs font-semibold border border-zinc-200 rounded focus:border-[#6043EF] outline-none disabled:opacity-50">
            {freqs.map((f) => <option key={f} value={f}>{FREQ_LABEL[f] || f}</option>)}
          </select>
          {sched.last_run
            ? <span className="text-[10px] text-zinc-400 ml-auto">last run {new Date(sched.last_run).toLocaleString("en-IN")}</span>
            : <span className="text-[10px] text-zinc-300 ml-auto">{sched.enabled ? "scheduled" : "manual only"}</span>}
        </div>

        {/* News demo dataset control (TV channel on /colleges) */}
        <div className="flex flex-wrap items-center gap-2 mb-5 rounded-xl bg-white border border-zinc-200 px-4 py-2.5 shadow-sm" data-testid="news-demo-control">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mr-auto">College News Channel (demo dataset)</span>
          <button onClick={async () => { await seedDemoNews(); }} data-testid="seed-news-demo-btn"
            className="h-8 px-3 text-xs font-bold uppercase bg-amber-500 text-white rounded hover:bg-amber-600">Seed news demo</button>
          <button onClick={async () => { await clearDemoNews(); }} data-testid="clear-news-demo-btn"
            className="h-8 px-3 text-xs font-bold uppercase bg-white text-rose-600 border border-rose-200 rounded hover:bg-rose-50">Clear news demo</button>
        </div>

        {tab === "counselling" && <ManualDiscovery />}

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Sources */}
          <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#09090B]">Sources</h2>
              {tab === "colleges" ? (
                <div className="flex items-center gap-2">
                  <input value={stateInput} onChange={(e) => setStateInput(e.target.value)} placeholder="state or all"
                    data-testid="crawler-state-input"
                    className="h-8 w-32 px-2 text-xs border border-zinc-200 rounded focus:border-[#6043EF] outline-none" />
                  <button onClick={handleRun} disabled={running} data-testid="crawler-run-btn"
                    className="h-8 px-3 text-xs font-bold uppercase bg-emerald-500 text-white rounded flex items-center gap-1 hover:bg-emerald-600 disabled:opacity-50">
                    <Play size={12} weight="fill" /> {running ? "Queued" : "Run"}
                  </button>
                </div>
              ) : (
                <button onClick={handleRun} disabled={running} data-testid={`crawler-run-${tab}-btn`}
                  className="h-8 px-3 text-xs font-bold uppercase bg-emerald-500 text-white rounded flex items-center gap-1 hover:bg-emerald-600 disabled:opacity-50">
                  <Play size={12} weight="fill" /> {running ? "Queued" : tab === "central" ? "Crawl now" : "Crawl all"}
                </button>
              )}
            </div>
            {tab === "counselling" && (
              <button onClick={openAddSource} data-testid="add-source-btn"
                className="mb-3 h-8 px-3 text-xs font-bold uppercase bg-[#6043EF] text-white rounded inline-flex items-center gap-1 hover:bg-[#4f36c9]">
                <Plus size={13} weight="bold" /> Add Source
              </button>
            )}
            {tab === "colleges" && summary && (
              <div className="mb-3 text-xs text-zinc-500">
                Crawled <b className="text-[#6043EF]">{summary.crawled}</b> / {summary.with_site} colleges with a website.
              </div>
            )}
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1" data-testid="crawler-sources-list">
              {sources.map((s, i) => (
                <div key={i} className="text-xs py-1.5 px-2 rounded hover:bg-zinc-50">
                  {(tab === "counselling" || tab === "central") && editingSrc === s.url ? (
                    <div className="flex flex-col gap-1.5" data-testid={`source-editor-${i}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 w-10 shrink-0">Name</span>
                        <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Display name"
                          data-testid={`source-edit-name-${i}`}
                          className="flex-1 h-7 px-2 rounded border border-zinc-300 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 w-10 shrink-0">URL</span>
                        <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="https://…"
                          data-testid={`source-edit-url-${i}`}
                          className="flex-1 h-7 px-2 rounded border border-zinc-300 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
                        <button onClick={() => saveEditSource(s)} disabled={savingSrc} title="Save"
                          data-testid={`source-edit-save-${i}`}
                          className="h-7 w-7 grid place-items-center rounded bg-[#6043EF] text-white disabled:opacity-50">
                          {savingSrc ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={13} weight="bold" />}
                        </button>
                        <button onClick={cancelEditSource} title="Cancel" className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100">
                          <XCircle size={13} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div className="flex items-center justify-between">
                  {tab === "colleges" ? (
                    <>
                      <span className="font-medium text-zinc-700">{s.state}</span>
                      <span className="text-zinc-400">
                        <b className={s.crawled >= s.with_site ? "text-emerald-600" : "text-amber-600"}>{s.crawled}</b>/{s.with_site}
                      </span>
                    </>
                  ) : tab === "central" ? (
                    <>
                      <span className="font-medium text-zinc-700 truncate flex-1 min-w-0">{s.short} · {s.authority}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {s.last_crawled_at && <span className="text-zinc-400 text-[10px] whitespace-nowrap" title={fmtIST(s.last_crawled_at)}>{fmtRel(s.last_crawled_at)}</span>}
                        <a href={s.url} target="_blank" rel="noreferrer" className={`hover:underline truncate max-w-[120px] ${/unreachable/i.test(s.status || "") ? "text-rose-500" : "text-[#6043EF]"}`}>{s.status || "idle"}</a>
                        <button onClick={() => startEditSource(s)} title="Edit source URL / name" data-testid={`edit-source-btn-${i}`}
                          className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-[#6043EF] hover:bg-zinc-100 shrink-0"><PencilSimple size={12} weight="bold" /></button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-zinc-700 truncate flex-1 min-w-0">{s.short} · {s.state}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {s.last_crawled_at && <span className="text-zinc-400 text-[10px] whitespace-nowrap" title={fmtIST(s.last_crawled_at)}>{fmtRel(s.last_crawled_at)}</span>}
                        <a href={s.url} target="_blank" rel="noreferrer" className={`hover:underline truncate max-w-[110px] ${/unreachable/i.test(s.status || "") ? "text-rose-500" : "text-[#6043EF]"}`}>{s.status || "idle"}</a>
                        <button onClick={() => startEditSource(s)} title="Edit source URL / name" data-testid={`edit-source-btn-${i}`}
                          className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-[#6043EF] hover:bg-zinc-100 shrink-0"><PencilSimple size={11} weight="bold" /></button>
                        <button onClick={() => crawlSource(s)} disabled={sourceRunning.has(s.url)} title="Crawl this source now"
                          data-testid={`crawl-source-btn-${i}`}
                          className="h-6 w-6 flex items-center justify-center rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-40 shrink-0">
                          {sourceRunning.has(s.url) ? <CircleNotch size={12} className="animate-spin" /> : <Play size={11} weight="fill" />}
                        </button>
                      </div>
                    </>
                  )}
                  </div>
                  )}
                </div>
              ))}
              {!sources.length && <div className="text-xs text-zinc-400 py-4 text-center">No sources.</div>}
            </div>
          </div>

          {/* Live log */}
          <div className="bg-[#0b1020] rounded-2xl border border-zinc-800 p-4 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <Broadcast size={14} weight="fill" className="text-emerald-400 animate-pulse" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">Live crawl log</span>
            </div>
            <div ref={logBox} className="font-mono text-[11px] leading-relaxed h-[60vh] overflow-y-auto space-y-0.5" data-testid="crawler-log-box">
              {logs.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-zinc-600 shrink-0">{fmtIST(l.ts)}</span>
                  <span className="text-cyan-400 shrink-0 truncate max-w-[30%]">{l.source}</span>
                  <span className={STATUS_COLOR[l.status] || "text-zinc-300"}>{l.message}</span>
                </div>
              ))}
              {!logs.length && <div className="text-zinc-600">Waiting for crawler activity… run a crawl to see live logs.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Add Source modal (counselling) */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="add-source-modal"
          onClick={() => !addBusy && setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Bank size={20} weight="fill" className="text-[#6043EF]" />
              <h3 className="text-base font-bold text-[#09090B] font-heading">Add Counselling Source</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Onboard a new state counselling authority. Only bodies present in the predictor but missing a crawler are shown.</p>

            <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">Counselling Body (State)</label>
            <select value={addForm.body} onChange={(e) => setAddForm((f) => ({ ...f, body: e.target.value }))}
              data-testid="add-source-body-select"
              className="w-full h-9 px-2 text-sm border border-zinc-200 rounded focus:border-[#6043EF] outline-none bg-white mb-3">
              <option value="">Select a body…</option>
              {missingBodies.map((b) => <option key={b.body} value={b.body}>{b.label}</option>)}
            </select>
            {!missingBodies.length && (
              <p className="text-[11px] text-emerald-600 -mt-2 mb-3">All predictor bodies already have a crawl source. 🎉</p>
            )}

            <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">Official Name</label>
            <input value={addForm.official_name} onChange={(e) => setAddForm((f) => ({ ...f, official_name: e.target.value }))}
              placeholder="e.g. CENTAC" data-testid="add-source-name-input"
              className="w-full h-9 px-2 text-sm border border-zinc-200 rounded focus:border-[#6043EF] outline-none mb-3" />

            <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">Official URL</label>
            <input value={addForm.official_url} onChange={(e) => setAddForm((f) => ({ ...f, official_url: e.target.value }))}
              placeholder="https://centacpuducherry.in/" data-testid="add-source-url-input"
              className="w-full h-9 px-2 text-sm border border-zinc-200 rounded focus:border-[#6043EF] outline-none mb-5" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} disabled={addBusy} data-testid="add-source-cancel"
                className="h-9 px-4 text-xs font-bold uppercase text-zinc-500 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-50">Cancel</button>
              <button onClick={submitAddSource} disabled={addBusy} data-testid="add-source-submit"
                className="h-9 px-4 text-xs font-bold uppercase bg-[#6043EF] text-white rounded inline-flex items-center gap-1.5 hover:bg-[#4f36c9] disabled:opacity-50">
                {addBusy ? <><CircleNotch size={13} className="animate-spin" /> Adding…</> : <><Plus size={13} weight="bold" /> Add & verify</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { status: "", year: "", body: "", doc_type: "", round_number: "", category: "", course: "", exam: "" };
// Default the filter panel to the current calendar year so operators land on
// "this year's docs" instead of an empty view. `EMPTY_FILTERS` remains the
// truly-empty baseline for the "Clear filters" button.
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_FILTERS = { ...EMPTY_FILTERS, year: String(CURRENT_YEAR) };

// Distinct, stable color per counselling body (deterministic hash → palette).
const BODY_PALETTE = [
  "bg-indigo-100 text-indigo-700",
  "bg-emerald-100 text-emerald-700",
  "bg-rose-100 text-rose-700",
  "bg-amber-100 text-amber-800",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
  "bg-fuchsia-100 text-fuchsia-700",
  "bg-lime-100 text-lime-800",
  "bg-cyan-100 text-cyan-700",
  "bg-pink-100 text-pink-700",
];
const bodyColor = (body) => {
  const s = String(body || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BODY_PALETTE[h % BODY_PALETTE.length];
};

// Ingest outcome tone for a doc row/card: "ok" (successful ingest) → light green,
// "error" (ingest failed OR a data doc that ingested 0 records/0 colleges) → light red.
const _isZeroIngest = (d) =>
  (d.ingest_records === 0 && d.ingest_colleges === 0) || /^0 records \/ 0 colleges/.test(d.ingest_note || "");
const rowTone = (d) => {
  if (d.status === "ingest_failed") return "error";
  if (d.status === "ingested") return (DATA_DOC_TYPES.includes(d.doc_type) && _isZeroIngest(d)) ? "error" : "ok";
  return "";
};

// Right slide-out drawer: all counselling bodies shown as <Short · State> (the same notation
// as the crawler Sources list) with per-type ingest counts; click a body to filter the docs.
function BodiesDrawer({ open, onClose, summary, active, onPick }) {
  const list = summary || [];
  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="bodies-backdrop" />}
      <div data-testid="bodies-drawer"
        className={`fixed top-0 right-0 h-full z-50 w-80 max-w-[88vw] bg-white border-l border-zinc-200 flex flex-col shadow-[-8px_0_30px_-12px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <span className="text-xs font-black uppercase tracking-wider text-[#09090B]">Counselling bodies</span>
          <button onClick={onClose} data-testid="bodies-drawer-close" className="w-7 h-7 flex items-center justify-center hover:bg-zinc-100 rounded"><XCircle size={16} weight="bold" /></button>
        </div>
        <div className="p-3 border-b border-zinc-100">
          <button onClick={() => onPick("")} data-testid="body-pick-all"
            className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors ${!active ? "bg-[#6043EF] text-white" : "text-zinc-600 hover:bg-zinc-50 border border-zinc-200"}`}>
            All bodies
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {list.map((e) => {
            const isActive = String(active) === String(e.body);
            const counts = e.counts || {};
            const hasCounts = Object.keys(counts).length > 0;
            return (
              <button key={e.body} onClick={() => onPick(e.body)} data-testid={`body-pick-${e.short || e.body}`}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all ${isActive ? "border-[#6043EF] bg-[#6043EF]/5 ring-1 ring-[#6043EF]/30" : "border-zinc-200 bg-white hover:border-[#6043EF]/40 hover:bg-zinc-50"}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${bodyColor(e.short || e.body)}`}>{e.short || e.body}</span>
                  {e.state && <span className="text-[10px] font-bold text-zinc-400">· {e.state}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {hasCounts
                    ? Object.entries(counts).map(([t, n]) => (
                        <span key={t} className="text-[9px] font-bold uppercase text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">{t.replace(/_/g, " ")} {n}</span>
                      ))
                    : <span className="text-[9px] font-bold uppercase text-zinc-300">no ingests yet</span>}
                </div>
              </button>
            );
          })}
          {list.length === 0 && <p className="text-[11px] text-zinc-300 px-1 py-4 text-center">No counselling bodies yet.</p>}
        </div>
      </div>
    </>
  );
}


// Manual discovery — paste URLs from known boards/official bodies (leaf PDF → classify
// & insert; folder → crawl seed). Self-contained so it can live on the Crawlers page.
export function ManualDiscovery() {
  const [mdText, setMdText] = useState("");
  const [mdResults, setMdResults] = useState(null);
  const [mdBusy, setMdBusy] = useState(false);
  const runDiscover = async () => {
    if (!mdText.trim()) return;
    setMdBusy(true);
    try {
      const r = await manualDiscoverCounselling(mdText);
      setMdResults(r.data.results || []);
    } catch (e) {
      setMdResults([{ url: "(request failed)", status: "unreachable", reason: String(e).slice(0, 80) }]);
    } finally { setMdBusy(false); }
  };
  return (
    <div className="mb-5 rounded-xl bg-white border border-zinc-200 p-4 shadow-sm" data-testid="manual-discover">
      <div className="flex items-center gap-2 mb-2">
        <MagicWand size={15} weight="fill" className="text-[#6043EF]" />
        <span className="text-[11px] font-black uppercase tracking-wider text-zinc-600">Manual discovery</span>
        <span className="text-[10px] text-zinc-400">Paste document/folder URLs (one per line) from known counselling boards or official bodies (NBE, NMC, NTA, MCC). Discovered docs land in “Documents crawled”.</span>
      </div>
      <textarea value={mdText} onChange={(e) => setMdText(e.target.value)} rows={3} data-testid="manual-discover-input"
        placeholder={"https://natboard.edu.in/viewNotice.php?...\nhttps://<board>/counselling/  (folder → crawl seed)"}
        className="w-full text-xs font-mono border border-zinc-200 rounded p-2 focus:border-[#6043EF] outline-none text-zinc-700 placeholder:text-zinc-300 resize-y" />
      <div className="flex items-center gap-2 mt-2">
        <button onClick={runDiscover} disabled={mdBusy || !mdText.trim()} data-testid="manual-discover-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-[#6043EF] text-white rounded flex items-center gap-1 hover:bg-[#4f36c9] disabled:opacity-50">
          <MagicWand size={13} weight="fill" /> {mdBusy ? "Classifying…" : "Discover & classify"}
        </button>
        {mdResults && (
          <button onClick={() => { setMdResults(null); setMdText(""); }} data-testid="manual-discover-clear"
            className="h-8 px-2 text-[10px] font-bold uppercase text-zinc-500 border border-zinc-200 rounded hover:bg-zinc-50">Clear results</button>
        )}
      </div>
      {mdResults && (
        <div className="mt-3 space-y-1.5" data-testid="manual-discover-results">
          {mdResults.map((r, i) => {
            const c = r.classification || {};
            const badge = { classified: "bg-emerald-100 text-emerald-700", already_in_place: "bg-amber-100 text-amber-700", queued_seed: "bg-indigo-100 text-indigo-700", rejected: "bg-rose-100 text-rose-700", unreachable: "bg-rose-100 text-rose-700" }[r.status] || "bg-zinc-100 text-zinc-600";
            return (
              <div key={i} className="flex items-start gap-2 text-[11px] border-b border-zinc-50 pb-1.5" data-testid={`md-result-${i}`}>
                <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${badge}`}>{(r.status || "").replace(/_/g, " ")}</span>
                <div className="min-w-0">
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-[#6043EF] hover:underline break-all">{r.url}</a>
                  <div className="text-[10px] text-zinc-500">
                    {r.status === "classified" && `${r.body} · ${(c.doc_type || "").replace(/_/g, " ")} · ${c.exam || ""} ${c.course ? "· " + c.course : ""} · ${c.year || ""}${r.ingestable ? " · INGESTABLE (ingest from Documents crawled)" : c.doc_type === "schedule" ? " · parse schedule from Documents crawled" : " · archived (info)"}`}
                    {r.status === "already_in_place" && `already in DB · ${(c.doc_type || "").replace(/_/g, " ")} · ${c.status}`}
                    {r.status === "queued_seed" && `folder → added as crawl seed (${r.body}) for next run`}
                    {(r.status === "rejected" || r.status === "unreachable") && r.reason}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export function DocsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [confirmUrl, setConfirmUrl] = useState("");
  // SM re-ingest round-confirmation dropdown state.
  const [smPlan, setSmPlan] = useState(null);       // { loading, url, auto_type, existing, choice }
  const openReingestConfirm = async (d) => {
    setConfirmUrl(d.url);
    if (!SM_DOC_TYPES.has(d.doc_type)) { setSmPlan(null); return; }
    setSmPlan({ loading: true, url: d.url });
    try {
      const { data } = await smIngestPlan(d.url);
      setSmPlan({ loading: false, url: d.url, ...data, choice: data.auto_type || "full" });
    } catch { setSmPlan({ loading: false, url: d.url, error: true, choice: "full" }); }
  };
  // SM-format LLM diff modal (shown when an SM ingest returned 0/0).
  const [smFmt, setSmFmt] = useState(null); // { loading, error, data }
  const [parseReason, setParseReason] = useState(null); // string popup
  // Agentic Fees tool modal (opened from a crawler-detected "fees" doc).
  const [feesSession, setFeesSession] = useState(null); // { id, body, filename }
  const openFeesTool = async (url) => {
    setBusy(`fees-${url}`);
    const t = toast.loading("Opening the Fees tool for this document…");
    try {
      const { data } = await ingestFeesDoc(url);
      toast.dismiss(t);
      if (data?.ok && data.session_id) {
        setFeesSession({ id: data.session_id, body: data.body, filename: data.filename });
      } else {
        toast.error(data?.error || "Could not open the Fees tool for this document.");
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || e?.message || "Could not open the Fees tool.");
    }
    setBusy("");
  };
  const runSmFormatCheck = async (url) => {
    setSmFmt({ loading: true });
    try {
      const { data } = await checkSmFormat(url);
      if (data?.ok) setSmFmt({ data });
      else setSmFmt({ error: data?.error || "Could not analyse this document." });
    } catch (e) {
      setSmFmt({ error: e?.response?.data?.error || e?.message || "Format check failed." });
    }
  };
  const openDocLink = async (o) => {
    if (!o) return;
    // Synthetic ASP.NET postback urls (…#pb=…) aren't directly openable — the PDF was
    // downloaded to storage at crawl time, so open the stored copy via a signed URL.
    const directUrl = o.url && !o.url.includes("#pb=") ? o.url : null;
    if (directUrl) { window.open(directUrl, "_blank"); return; }
    if (o.local_path) {
      const t = toast.loading("Opening…");
      try { const r = await getSignedDocUrl(o.local_path); window.open(r.data.url, "_blank"); toast.dismiss(t); }
      catch { toast.dismiss(t); toast.error("Could not open document"); }
      return;
    }
    if (o.url) window.open(o.url, "_blank");
  };
  const [showIgnored, setShowIgnored] = useState(false);
  const [bodiesOpen, setBodiesOpen] = useState(false);

  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
      const r = await getCounsellingDocs(params);
      // Ignore stale/out-of-order responses (e.g. a slow all-bodies poll that
      // resolves after the user already switched the body filter) so old rows
      // can never repaint over the freshly-filtered set.
      if (seq === reqSeq.current) setData(r.data);
    } catch (e) { /* noop */ }
  }, [filters]);
  useEffect(() => {
    // Clear stale rows the instant the filter changes so the previous body's
    // docs don't linger while the new fetch is in flight, then (re)start polling.
    setData(null);
    load();
    const id = setInterval(load, 16000);
    return () => clearInterval(id);
  }, [load]);

  const rawDocs = data?.docs || [];
  const counts = data?.counts || {};
  const facets = data?.facets || {};
  const season = data?.season;
  const autoApprove = !!data?.auto_approve;
  const currentYear = data?.current_year;
  const byBody = data?.by_body || {};
  const bodiesSummary = data?.bodies_summary || [];
  const activeBodyShort = (bodiesSummary.find((x) => x.body === filters.body) || {}).short
    || (filters.body ? String(filters.body).replace(/_/g, " ") : "");
  const activeFilters = Object.values(filters).some((v) => v !== "" && v != null);

  // client-side column sort (Body / State / Year / Round / Type / Status)
  const SORT_FIELD = { body: "short", state: "state", year: "year", round: "round_number", type: "doc_type", status: "status", class: "category" };
  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  // Historical counselling view (past-year chip) → show only detected+ingested, hide ignored/rejected.
  const isHistCounselling = filters.category === "counselling" && filters.year && currentYear && Number(filters.year) < Number(currentYear);
  // Default view (all years): hide ignored/rejected unless the "Show ignored" toggle is on
  // (or an explicit status filter is chosen). Historical view additionally hides `information`.
  const rawVisible = rawDocs.filter((d) => {
    if (isHistCounselling && d.doc_type === "information") return false;
    if (!showIgnored && !filters.status && ["ignored", "rejected"].includes(d.status)) return false;
    return true;
  });
  const docs = (() => {
    if (!sort.key) return rawVisible;
    const f = SORT_FIELD[sort.key];
    const mul = sort.dir === "asc" ? 1 : -1;
    return [...rawVisible].sort((a, b) => {
      let av = a[f], bv = b[f];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
  })();

  const act = async (fn, key) => { setBusy(key); try { await fn(); await load(); } finally { setBusy(""); } };
  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const SortHead = ({ sk, label }) => (
    <th className="pr-2 cursor-pointer select-none hover:text-[#6043EF]" onClick={() => toggleSort(sk)} data-testid={`sort-${sk}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        <span className={sort.key === sk ? "text-[#6043EF]" : "text-zinc-300"}>{sort.key === sk ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </span>
    </th>
  );

  const [docsView, setDocsView] = useState(() => localStorage.getItem("docsView") || "row");
  const toggleDocsView = (v) => { setDocsView(v); localStorage.setItem("docsView", v); };
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [addDocUrl, setAddDocUrl] = useState("");
  const [addDocBusy, setAddDocBusy] = useState(false);
  const [highlightDoc, setHighlightDoc] = useState(null);
  useEffect(() => {
    if (!highlightDoc) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-doc-url="${CSS.escape(highlightDoc)}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    const clear = setTimeout(() => setHighlightDoc(null), 4000);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [highlightDoc]);
  const submitManualDoc = async () => {
    const u = addDocUrl.trim();
    if (!u) return;
    setAddDocBusy(true);
    try {
      const r = await manualDiscoverCounselling(u, true);
      const res = (r.data.results || [])[0] || {};
      if (res.status === "classified") {
        const c = res.classification || {};
        toast.success(`Added as "${c.doc_type || "document"}" — set the type or run actions below`);
        setAddDocUrl(""); setAddDocOpen(false);
        setHighlightDoc(u);
        await load();
      } else if (res.status === "already_in_place") {
        toast.info("This PDF is already in the documents list — highlighting it");
        setAddDocUrl(""); setAddDocOpen(false);
        setHighlightDoc(u);
        await load();
      } else if (res.status === "queued_seed") {
        toast.success("Folder URL registered as a crawl seed for the next run");
        setAddDocUrl(""); setAddDocOpen(false);
      } else {
        toast.error(res.reason || `Could not add (${res.status || "failed"})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that URL");
    } finally {
      setAddDocBusy(false);
    }
  };

  // Shared action buttons (identical in row + card views).
  const renderActions = (d) => (
    d.status === "ingesting" ? (
      <IngestProgress d={d} onDone={load} />
    ) : confirmUrl === d.url ? (
      SM_DOC_TYPES.has(d.doc_type) ? (
        <div className="flex flex-col gap-1.5 items-end min-w-[280px]" data-testid={`reingest-confirm-${d.url}`}>
          <span className="text-[9px] text-rose-600 font-bold uppercase">Confirm the round before overwriting seats</span>
          {smPlan?.loading ? (
            <span className="text-[10px] text-zinc-400">Checking existing seat data…</span>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-zinc-500">Ingest as</span>
                <select value={smPlan?.choice || "full"} data-testid={`reingest-round-select-${d.url}`}
                  onChange={(e) => setSmPlan((p) => ({ ...(p || {}), choice: e.target.value }))}
                  className="h-7 px-1.5 text-[11px] font-bold border border-zinc-300 rounded bg-white">
                  {SM_VARIETIES.map((t) => {
                    const ex = smPlan?.existing?.[t];
                    return <option key={t} value={t}>{SM_VARIETY_LABEL(t)}{ex ? ` — has ${ex.rows} rows / ${ex.colleges} colleges` : " — empty"}{smPlan?.auto_type === t ? " (auto)" : ""}</option>;
                  })}
                </select>
              </div>
              {(() => {
                const ch = smPlan?.choice || "full";
                const ex = smPlan?.existing?.[ch];
                return ex
                  ? <span className="text-[9px] text-rose-600 font-bold" data-testid={`reingest-overwrite-warn-${d.url}`}>⚠ {SM_VARIETY_LABEL(ch)} already has {ex.rows} rows — this OVERWRITES them</span>
                  : <span className="text-[9px] text-emerald-600 font-bold">{SM_VARIETY_LABEL(ch)} is empty — safe to write</span>;
              })()}
              <div className="flex gap-1">
                <button onClick={() => { const ch = smPlan?.choice || "full"; setConfirmUrl(""); setSmPlan(null); act(() => ingestSmWithRound(d.url, ch), `a-${d.url}`); }}
                  data-testid={`confirm-reingest-${d.url}`}
                  className="h-7 px-2 text-[10px] font-black uppercase bg-rose-500 text-white rounded hover:bg-rose-600">Ingest this round</button>
                <button onClick={() => { setConfirmUrl(""); setSmPlan(null); }} data-testid={`cancel-reingest-${d.url}`}
                  className="h-7 px-2 text-[10px] font-bold uppercase bg-white text-zinc-500 border border-zinc-200 rounded hover:bg-zinc-50">Cancel</button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex gap-1 justify-end items-center flex-wrap" data-testid={`reingest-confirm-${d.url}`}>
          <span className="text-[9px] text-rose-600 font-bold uppercase">Overwrite ingested data?</span>
          <button onClick={() => { setConfirmUrl(""); act(() => approveCounsellingDocs([d.url], true), `a-${d.url}`); }}
            data-testid={`confirm-reingest-${d.url}`}
            className="h-7 px-2 text-[10px] font-black uppercase bg-rose-500 text-white rounded hover:bg-rose-600">Yes, re-ingest</button>
          <button onClick={() => setConfirmUrl("")} data-testid={`cancel-reingest-${d.url}`}
            className="h-7 px-2 text-[10px] font-bold uppercase bg-white text-zinc-500 border border-zinc-200 rounded hover:bg-zinc-50">Cancel</button>
        </div>
      )
    ) : (
      <div className="flex gap-1 justify-end items-center flex-wrap">
        {d.doc_type === "fees" && (
          <button onClick={() => openFeesTool(d.url)} disabled={busy === `fees-${d.url}`}
            data-testid={`ingest-fees-${d.url}`}
            title="Open the agentic Fees tool for this document (chat to confirm year & pages)"
            className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-[#6043EF] text-white rounded hover:bg-[#4c34c9] disabled:opacity-50">
            <MagicWand size={12} weight="fill" /> {busy === `fees-${d.url}` ? "Opening…" : "Ingest (Fees tool)"}
          </button>
        )}
        {d.doc_type === "schedule" && (
          <>
            {d.schedule_year && (
              <a href={`/counselling/schedule/${(d.short || "").toLowerCase()}/${d.schedule_stream || "ug"}-${d.schedule_year}`}
                target="_blank" rel="noreferrer" data-testid={`view-schedule-${d.url}`}
                className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-white text-[#6043EF] border border-[#6043EF]/30 rounded hover:bg-violet-50">
                <Sparkle size={12} weight="fill" /> View page
              </a>
            )}
            <button onClick={() => act(() => parseSchedule(d.url), `p-${d.url}`)} disabled={busy === `p-${d.url}`}
              data-testid={`parse-schedule-${d.url}`}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-violet-500 text-white rounded hover:bg-violet-600 disabled:opacity-50">
              <MagicWand size={12} weight="fill" /> {busy === `p-${d.url}` ? "Parsing…" : d.schedule_year ? "Re-parse" : "Parse schedule"}
            </button>
            {d.schedule_year && (
              <button onClick={() => act(() => setScheduleVisibility(d.url, !d.schedule_hidden), `h-${d.url}`)} disabled={busy === `h-${d.url}`}
                data-testid={`toggle-schedule-visibility-${d.url}`}
                title={d.schedule_hidden ? "Show on home Live Status dashboard" : "Hide from home Live Status dashboard (e.g. misclassified)"}
                className={`inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase rounded border disabled:opacity-50 ${d.schedule_hidden ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}>
                {d.schedule_hidden ? <><Eye size={12} weight="fill" /> {busy === `h-${d.url}` ? "…" : "Hidden — Show"}</> : <><EyeSlash size={12} weight="fill" /> {busy === `h-${d.url}` ? "…" : "Hide"}</>}
              </button>
            )}
          </>
        )}
        {d.status === "detected" && DATA_DOC_TYPES.includes(d.doc_type) && (
          <>
            <button onClick={() => (d.reingest ? openReingestConfirm(d) : act(() => approveCounsellingDocs([d.url], true), `a-${d.url}`))} disabled={busy === `a-${d.url}`}
              data-testid={`approve-doc-${d.url}`}
              className={`inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase text-white rounded disabled:opacity-50 ${d.reingest ? "bg-rose-500 hover:bg-rose-600" : d.prior_year ? "bg-indigo-500 hover:bg-indigo-600" : "bg-emerald-500 hover:bg-emerald-600"}`}>
              <CheckCircle size={12} weight="fill" /> {d.reingest ? "Re-ingest" : d.prior_year ? "Ingest" : "Approve"}
            </button>
            <button onClick={() => act(() => rejectCounsellingDocs([d.url]), `r-${d.url}`)} disabled={busy === `r-${d.url}`}
              data-testid={`reject-doc-${d.url}`}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-white text-zinc-500 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-50">
              <XCircle size={12} weight="bold" /> Reject
            </button>
          </>
        )}
        {d.status === "ingested" && DATA_DOC_TYPES.includes(d.doc_type) && (
          <>
            {(d.ingest_records === 0 && d.ingest_colleges === 0) || /^0 records \/ 0 colleges/.test(d.ingest_note || "") ? (
              <>
                {d.parse_reason && (
                  <button onClick={() => setParseReason(d.parse_reason)}
                    data-testid={`parse-reason-${d.url}`}
                    title="Why did this parse 0 rows? (click)"
                    className="inline-flex items-center justify-center h-7 w-7 text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded">
                    <WarningCircle size={16} weight="fill" />
                  </button>
                )}
                <button onClick={() => runSmFormatCheck(d.url)} disabled={busy === `a-${d.url}`}
                  data-testid={`check-sm-format-${d.url}`}
                  title="0 rows parsed — ask AI whether the seat-matrix format changed vs last year"
                  className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50">
                  <MagicWand size={12} weight="fill" /> Check SM Format
                </button>
              </>
            ) : counsellingViewHref(d) && (
              <a href={counsellingViewHref(d)} target="_blank" rel="noreferrer"
                data-testid={`view-counselling-${d.url}`}
                title="Open /counselling page filtered to this body / year / round"
                className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-white text-[#6043EF] border border-[#6043EF]/30 rounded hover:bg-violet-50">
                <Sparkle size={12} weight="fill" /> View data
              </a>
            )}
            <button onClick={() => openReingestConfirm(d)} disabled={busy === `a-${d.url}`}
              data-testid={`reingest-doc-${d.url}`}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-white text-rose-600 border border-rose-200 rounded hover:bg-rose-50 disabled:opacity-50">
              <FileArrowDown size={12} weight="bold" /> {busy === `a-${d.url}` ? "Re-ingesting…" : "Re-ingest"}
            </button>
          </>
        )}
        {d.status === "detected" && !DATA_DOC_TYPES.includes(d.doc_type) && d.doc_type !== "schedule" && (
          <span className="text-[9px] font-bold uppercase text-zinc-300" data-testid={`no-ingest-${d.url}`}>not ingested</span>
        )}
        {(d.status === "ingest_failed" || (d.status === "cancelled" && DATA_DOC_TYPES.includes(d.doc_type))) && (
          <>
            <button onClick={() => act(() => approveCounsellingDocs([d.url], true), `a-${d.url}`)} disabled={busy === `a-${d.url}`}
              data-testid={`retry-doc-${d.url}`}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50">
              <FileArrowDown size={12} weight="bold" /> {busy === `a-${d.url}` ? "Ingesting…" : (d.status === "cancelled" ? "Ingest" : "Retry")}
            </button>
            {d.status === "ingest_failed" && d.ingest_note && (
              <div className="w-full text-right text-[9px] font-bold text-rose-600 break-words leading-snug"
                data-testid={`ingest-error-${d.url}`} title={d.ingest_note}>Error : "{d.ingest_note}"</div>
            )}
          </>
        )}
        {d.doc_type === "nmc_seat_matrix" && d.status !== "detected" && (
          <button onClick={() => act(() => reingestNmcDoc(d.url, true), `nre-${d.url}`)} disabled={busy === `nre-${d.url}`}
            data-testid={`nmc-reingest-hint-${d.url}`}
            title={d.table_parsing_hint ? `Re-ingest using saved hint: ${d.table_parsing_hint}` : "Re-ingest with Step-2 validators (no hint saved)"}
            className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase bg-violet-500 text-white rounded hover:bg-violet-600 disabled:opacity-50">
            <MagicWand size={12} weight="fill" /> {busy === `nre-${d.url}` ? "Kicked off…" : "Re-ingest (Step 2)"}
          </button>
        )}
      </div>
    )
  );

  // Shared reader-friendly document info (labels, title, type/round/class, type-change + hint).
  const renderDocInfo = (d) => (
    <div className="space-y-2 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ${bodyColor(d.short)}`} data-testid={`body-${d.url}`}>{d.short || "—"}</span>
          {d.state && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide bg-slate-100 text-slate-600" data-testid={`state-${d.url}`}>{d.state}</span>}
          {d.exam && <span className="text-[8px] font-black uppercase text-zinc-400" data-testid={`exam-${d.url}`}>{d.exam.replace(/_/g, " ")}</span>}
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-100 text-amber-700 shrink-0 font-mono tabular-nums" data-testid={`year-${d.url}`}>
          {d.year}{d.prior_year && <span className="text-[8px]" data-testid={`prior-year-${d.url}`}>prior</span>}
        </span>
      </div>

      <a href={d.url && d.url.includes("#pb=") ? undefined : d.url} target="_blank" rel="noreferrer"
        onClick={d.url && d.url.includes("#pb=") ? (e) => { e.preventDefault(); openDocLink(d); } : undefined}
        className="block text-[#6043EF] hover:underline font-semibold text-sm sm:text-[15px] leading-snug break-words cursor-pointer" data-testid={`doc-title-${d.url}`}>
        {d.title || d.url}
        {d.demo && <span className="ml-1.5 text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1 rounded align-middle">demo</span>}
      </a>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-bold text-zinc-700">{(d.doc_type || "").replace(/_/g, " ") || "—"}</span>
          {d.classification_confidence && (
            <span className={`text-[8px] font-black uppercase px-1 rounded ${d.classification_confidence === "high" ? "bg-emerald-100 text-emerald-700" : d.classification_confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}
              data-testid={`conf-${d.url}`} title={`${d.classification_method || ""} · ${d.classify_note || ""}`}>
              {d.classification_confidence}{d.classification_method === "llm" ? " ᴬᴵ" : ""}
            </span>
          )}
          {d.round_number && <span className="text-[10px] font-semibold text-zinc-500">Round {d.round_number}</span>}
          {d.doc_type_override && <span className="text-[8px] font-black uppercase text-[#6043EF]" data-testid={`type-overridden-${d.url}`}>overridden</span>}
          {d.status === "ingesting" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700"><CircleNotch size={10} weight="bold" className="animate-spin" /> ingesting</span>
          ) : (
            <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold ${DOC_STATUS_STYLE[d.status] || "bg-zinc-100 text-zinc-600"}`}>{d.status}</span>
          )}
          {d.reingest && <span className="text-[8px] font-black uppercase bg-orange-100 text-orange-700 px-1 rounded" title="This slot is already ingested — this would be a re-ingest">re-ingest</span>}
        </div>
        <div data-testid={`class-${d.url}`}>
          {d.category && d.category !== "counselling" ? (
            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-orange-100 text-orange-700" title="Non-counselling category (excluded from NEET)">
              {d.category.replace(/_/g, " ")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-emerald-100 text-emerald-700" title="Counselling / admission document">counselling</span>
              {d.course && <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-indigo-100 text-indigo-700" title="Admission course/program">{d.course.replace(/_/g, " ")}</span>}
            </span>
          )}
        </div>
      </div>

      {d.ingest_note && d.status !== "ingest_failed" && <div className="text-[9px] text-zinc-400">{d.ingest_note}</div>}

      <div className="flex items-start gap-2 flex-wrap pt-1">
        <select value={d.doc_type || ""} onChange={(e) => act(() => setCounsellingDocType(d.url, e.target.value), `t-${d.url}`)}
          disabled={busy === `t-${d.url}`} data-testid={`type-override-${d.url}`} title="Override type (e.g. treat as schedule)"
          className="h-7 text-[10px] border border-zinc-200 rounded bg-white text-zinc-600 px-1 focus:border-[#6043EF] outline-none shrink-0">
          {DOC_TYPE_OPTIONS.map((o) => <option key={o} value={o}>set: {o.replace(/_/g, " ")}</option>)}
        </select>
        <div className="flex-1 min-w-[180px]">
          <HintCell url={d.url} initial={d.misclass_hint || ""} />
          {d.doc_type === "nmc_seat_matrix" && <TableHintCell url={d.url} initial={d.table_parsing_hint || ""} />}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-5 bg-white rounded-2xl border border-zinc-200 p-5 shadow-sm" data-testid="counselling-docs-panel">
      {/* controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#09090B] mr-auto flex items-center gap-2">
          <FileArrowDown size={16} weight="bold" className="text-[#6043EF]" /> Detected Documents
          {currentYear && (
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-[#6043EF] text-white px-2 py-0.5 rounded-full" data-testid="current-year-badge">
              current yr {currentYear}
            </span>
          )}
          <span className="text-[10px] font-semibold text-zinc-400 normal-case tracking-normal">
            {Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(" · ") || "none yet"}
          </span>
        </h2>

        {/* auto-approve switch */}
        <button onClick={() => act(() => setCounsellingAutoApprove(!autoApprove), "auto")} disabled={busy === "auto"}
          data-testid="auto-approve-toggle"
          className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${autoApprove ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200"}`}>
          <span className={`w-2 h-2 rounded-full ${autoApprove ? "bg-white animate-pulse" : "bg-zinc-400"}`} />
          Auto-approve {autoApprove ? "ON" : "OFF"}
        </button>

        <button onClick={() => act(() => runSeasonPrimer(2026), "season")} disabled={busy === "season"}
          data-testid="season-primer-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-violet-500 text-white rounded flex items-center gap-1 hover:bg-violet-600 disabled:opacity-50">
          <MagicWand size={13} weight="fill" /> {busy === "season" ? "Checking…" : "Season primer"}
        </button>
        <button onClick={() => act(relinkCounselling, "relink")} disabled={busy === "relink"}
          data-testid="relink-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-sky-500 text-white rounded flex items-center gap-1 hover:bg-sky-600 disabled:opacity-50">
          <MagicWand size={13} weight="fill" /> {busy === "relink" ? "Re-linking…" : "Re-link classify"}
        </button>
        <button onClick={() => act(classifyCounselling, "classify")} disabled={busy === "classify"}
          data-testid="classify-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-fuchsia-500 text-white rounded flex items-center gap-1 hover:bg-fuchsia-600 disabled:opacity-50">
          <MagicWand size={13} weight="fill" /> {busy === "classify" ? "Queued…" : "Classify pending"}
        </button>
        <button onClick={() => act(runEodSweep, "eod")} disabled={busy === "eod"}
          data-testid="run-eod-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-sky-500 text-white rounded flex items-center gap-1 hover:bg-sky-600 disabled:opacity-50">
          <Clock size={13} weight="fill" /> Run EOD sweep
        </button>
        <button onClick={() => act(seedDemoSnapshot, "seed")} disabled={busy === "seed"}
          data-testid="seed-demo-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-amber-500 text-white rounded flex items-center gap-1 hover:bg-amber-600 disabled:opacity-50">
          <Sparkle size={13} weight="fill" /> Seed demo
        </button>
        <button onClick={() => act(clearDemoSnapshot, "clear")} disabled={busy === "clear"}
          data-testid="clear-demo-btn"
          className="h-8 px-3 text-xs font-bold uppercase bg-white text-rose-600 border border-rose-200 rounded flex items-center gap-1 hover:bg-rose-50 disabled:opacity-50">
          <Trash size={13} weight="bold" /> Clear demo
        </button>
      </div>

      {season && (
        <div className="mb-3 text-xs text-zinc-600 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2" data-testid="season-status">
          Season <b className={season.active ? "text-emerald-600" : "text-amber-600"}>{season.active ? "ACTIVE" : "pre-season"}</b>
          {" · "}phase: <b>{season.phase}</b>{season.note ? ` · ${season.note}` : ""}
          <span className="text-zinc-400"> (crawl frequency set to {season.active ? "daily" : "weekly"})</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="docs-filter-bar">
        <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Filter</span>
        {[
          // Status dropdown hidden per UX (default hides ignored/rejected via the toggle below).
          // Code preserved — flip the `false &&` to re-enable per-status filtering later.
          ...(false ? [{ k: "status", label: "Status", opts: facets.statuses }] : []),
          { k: "year", label: "Year", opts: facets.years },
          { k: "body", label: "Body", opts: facets.bodies },
          { k: "exam", label: "Exam", opts: facets.exams },
          { k: "doc_type", label: "Type", opts: facets.doc_types },
          { k: "round_number", label: "Round", opts: facets.rounds },
          { k: "category", label: "Category", opts: facets.categories },
          { k: "course", label: "Course", opts: facets.courses },
        ].map((f) => (
          <select key={f.k} value={filters[f.k]} onChange={(e) => setF(f.k, e.target.value)} data-testid={`docs-filter-${f.k}`}
            className="h-8 px-2 text-xs font-semibold border border-zinc-200 rounded focus:border-[#6043EF] outline-none bg-white text-zinc-600">
            <option value="">{f.label}: all</option>
            {(f.opts || []).map((o) => (
              <option key={String(o)} value={o}>{String(o).replace(/_/g, " ")}</option>
            ))}
          </select>
        ))}
        {/* Ignored/rejected are hidden by default; this toggle reveals them. */}
        <button onClick={() => setShowIgnored((v) => !v)} data-testid="show-ignored-toggle"
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${showIgnored ? "bg-zinc-700 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200"}`}>
          <span className={`w-2 h-2 rounded-full ${showIgnored ? "bg-white" : "bg-zinc-400"}`} />
          Show ignored {showIgnored ? "ON" : "OFF"}
        </button>
        {activeFilters && (
          <button onClick={() => setFilters(EMPTY_FILTERS)} data-testid="docs-filter-clear"
            className="h-8 px-2 text-[10px] font-bold uppercase text-rose-600 border border-rose-200 rounded hover:bg-rose-50">
            Clear
          </button>
        )}
        <span className="text-[10px] text-zinc-400 ml-auto">{docs.length} shown</span>
        <div className="inline-flex rounded-full border border-zinc-200 overflow-hidden" data-testid="docs-view-toggle">
          <button onClick={() => toggleDocsView("card")} data-testid="docs-view-card"
            className={`h-8 px-3 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition-colors ${docsView === "card" ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}>
            <SquaresFour size={13} weight="bold" /> Card
          </button>
          <button onClick={() => toggleDocsView("row")} data-testid="docs-view-row"
            className={`h-8 px-3 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition-colors ${docsView === "row" ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}>
            <Rows size={13} weight="bold" /> Row
          </button>
        </div>
      </div>

      {/* Year quick-links + bulk historical ingestion (reuses the same ingest engine).
          One click filters to counselling feeds of a year; "Ingest" bulk-ingests its
          DATA docs as a background job (skips already-ingested slots). */}
      <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl bg-emerald-50/60 border border-emerald-100 px-4 py-2.5" data-testid="year-ingest-strip">
        <span className="text-[11px] font-black uppercase tracking-wider text-emerald-700 mr-1">Historical Counselling feeds by year</span>
        {(facets.years || []).filter((y) => currentYear && Number(y) < Number(currentYear)).map((y) => {
          const activeY = String(filters.year) === String(y);
          return (
            <button key={y} onClick={() => setFilters({ ...EMPTY_FILTERS, category: "counselling", year: String(y) })}
              data-testid={`year-chip-${y}`}
              className={`h-7 px-3 text-xs font-bold rounded-full border border-emerald-200 ${activeY ? "bg-emerald-500 text-white" : "bg-white text-emerald-700 hover:bg-emerald-100"}`}>
              {y}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-zinc-500">DATA docs only · skips already-ingested · background job{filters.body ? ` · ${filters.body}` : ""}</span>
          <button onClick={() => act(() => ingestCounsellingYear(filters.year, filters.body || undefined), `ing-${filters.year}`)}
            disabled={!isHistCounselling || busy === `ing-${filters.year}`} data-testid="ingest-selected-year-btn"
            className="h-8 px-3 text-xs font-black uppercase bg-emerald-600 text-white rounded flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-40">
            <CheckCircle size={13} weight="fill" /> {busy === `ing-${filters.year}` ? "Queued…" : isHistCounselling ? `Ingest ${filters.year} feeds` : "Select a year"}
          </button>
        </div>
      </div>

      {/* docs — card view (reader-friendly, default) */}
      {docsView === "card" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="docs-card-grid">
          {docs.map((d, i) => {
            const tone = rowTone(d);
            const toneCls = tone === "ok" ? "border-emerald-200 bg-emerald-50" : tone === "error" ? "border-rose-200 bg-rose-50" : "border-zinc-200 bg-white";
            return (
            <div key={d.url} data-doc-url={d.url} className={`rounded-xl border ${toneCls} p-4 flex flex-col gap-3 hover:border-[#6043EF]/40 hover:shadow-sm transition-all ${highlightDoc === d.url ? "ring-2 ring-[#6043EF]" : ""}`} data-testid={`doc-card-${d.url}`}>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-mono tabular-nums text-zinc-300 pt-0.5 shrink-0">#{i + 1}</span>
                <div className="flex-1 min-w-0">{renderDocInfo(d)}</div>
              </div>
              <div className="pt-2 border-t border-zinc-100">{renderActions(d)}</div>
            </div>
            );
          })}
          {!docs.length && (
            <div className="col-span-full py-6 text-center text-zinc-400">No documents detected yet. Run a counselling crawl or seed a demo snapshot.</div>
          )}
        </div>
      )}

      {/* docs — row view (Sr.No | Document | Actions) */}
      {docsView === "row" && (
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="counselling-docs-table">
          <thead>
            <tr className="text-left text-[10px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
              <th className="py-2 pr-2 w-8">Sr.No.</th>
              <th className="py-2 pr-2">
                <span className="inline-flex items-center gap-1.5">
                  Document
                  <button onClick={() => setAddDocOpen((o) => !o)} title="Add a document by direct PDF link"
                    data-testid="add-manual-doc-btn"
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#6043EF] text-white hover:bg-[#4f36c9] transition-colors">
                    <Plus size={11} weight="bold" />
                  </button>
                </span>
              </th>
              <th className="pr-2 whitespace-nowrap">Added / Crawled</th>
              <th className="pr-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {addDocOpen && (
              <tr className="bg-[#6043EF]/5" data-testid="add-manual-doc-row">
                <td className="py-3 pr-2 align-top w-8 text-[#6043EF]"><Plus size={13} weight="bold" /></td>
                <td className="py-3 pr-4 align-top" colSpan={3}>
                  <div className="flex items-center gap-2">
                    <input autoFocus value={addDocUrl} onChange={(e) => setAddDocUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") submitManualDoc(); if (e.key === "Escape") { setAddDocOpen(false); setAddDocUrl(""); } }}
                      placeholder="https://<counselling-board>/notice.pdf  (direct PDF link)"
                      data-testid="add-manual-doc-input"
                      className="flex-1 h-8 text-xs font-mono border border-zinc-300 rounded px-2 focus:border-[#6043EF] outline-none" />
                    <button onClick={submitManualDoc} disabled={addDocBusy || !addDocUrl.trim()} data-testid="add-manual-doc-submit"
                      className="h-8 px-3 text-[11px] font-bold uppercase bg-[#6043EF] text-white rounded flex items-center gap-1 hover:bg-[#4f36c9] disabled:opacity-50">
                      {addDocBusy ? <><CircleNotch size={12} className="animate-spin" /> Classifying…</> : <>Fetch & classify</>}
                    </button>
                    <button onClick={() => { setAddDocOpen(false); setAddDocUrl(""); }} className="h-8 w-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700"><XCircle size={15} /></button>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">We'll fetch the PDF, content-classify it, and add it here as a manual entry — then you can change its type and run parse / SM / allotment ingest.</p>
                </td>
              </tr>
            )}
            {docs.map((d, i) => {
              const tone = rowTone(d);
              const toneCls = tone === "ok" ? "bg-emerald-50 hover:bg-emerald-100" : tone === "error" ? "bg-rose-50 hover:bg-rose-100" : "hover:bg-zinc-50";
              return (
              <tr key={d.url} data-doc-url={d.url} className={`${toneCls} align-top transition-shadow ${highlightDoc === d.url ? "ring-2 ring-[#6043EF] ring-inset" : ""}`} data-testid={`doc-row-${d.url}`}>
                <td className="py-3 pr-2 text-[11px] font-mono tabular-nums text-zinc-400 align-top w-8">{i + 1}</td>
                <td className="py-3 pr-4 align-top w-[70%]">{renderDocInfo(d)}</td>
                <td className="py-3 pr-2 align-top text-[11px] text-zinc-500 whitespace-nowrap" data-testid={`doc-date-${d.url}`}>
                  {(() => {
                    const dt = d.detected_at || d.discovered_at || d.published_at;
                    if (!dt) return <span className="text-zinc-300">—</span>;
                    return (
                      <span className="inline-flex flex-col">
                        <span>{new Date(dt).toLocaleDateString()}</span>
                        <span className="text-[9px] uppercase tracking-wider font-bold text-zinc-400">{d.source === "manual" ? "Manual" : "Crawled"}</span>
                      </span>
                    );
                  })()}
                </td>
                <td className="pr-2 align-top text-right whitespace-nowrap">{renderActions(d)}</td>
              </tr>
              );
            })}
            {!docs.length && (
              <tr><td colSpan={4} className="py-6 text-center text-zinc-400">No documents detected yet. Run a counselling crawl or seed a demo snapshot.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {parseReason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="parse-reason-modal" onClick={() => setParseReason(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <WarningCircle size={16} weight="fill" className="text-amber-500" />
                <span className="text-sm font-black uppercase tracking-wider text-[#09090B]">Why 0 rows parsed</span>
              </div>
              <button onClick={() => setParseReason(null)} data-testid="parse-reason-close" className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100"><XCircle size={16} weight="bold" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#3F3F46] leading-relaxed" data-testid="parse-reason-text">{parseReason}</p>
            </div>
          </div>
        </div>
      )}
      {feesSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="fees-tool-modal" onClick={() => setFeesSession(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 shrink-0">
              <div className="flex items-center gap-2">
                <MagicWand size={16} weight="fill" className="text-[#6043EF]" />
                <span className="text-sm font-black uppercase tracking-wider text-[#09090B]">Fees Tool</span>
                <span className="text-xs text-zinc-500 truncate max-w-[360px]">{feesSession.body} · {feesSession.filename}</span>
              </div>
              <button onClick={() => setFeesSession(null)} data-testid="fees-tool-close" className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100"><XCircle size={16} weight="bold" /></button>
            </div>
            <div className="flex-1 min-h-0">
              <AgentChat initialSessionId={feesSession.id} hideSessions />
            </div>
          </div>
        </div>
      )}

      {smFmt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="sm-format-modal" onClick={() => setSmFmt(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-2">
                <MagicWand size={16} weight="fill" className="text-amber-500" />
                <span className="text-sm font-black uppercase tracking-wider text-[#09090B]">SM Format Check</span>
              </div>
              <button onClick={() => setSmFmt(null)} data-testid="sm-format-close" className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100"><XCircle size={16} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              {smFmt.loading && (
                <div className="flex items-center gap-2 text-sm text-zinc-500 py-8 justify-center" data-testid="sm-format-loading">
                  <CircleNotch size={18} weight="bold" className="animate-spin" /> Comparing this year's format with last year…
                </div>
              )}
              {smFmt.error && <div className="text-sm text-rose-600 py-4" data-testid="sm-format-error">{smFmt.error}</div>}
              {smFmt.data && (() => { const r = smFmt.data; return (
                <div className="space-y-4" data-testid="sm-format-result">
                  <div className="flex items-center gap-2">
                    {r.same_format === false && <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-rose-100 text-rose-700">Format changed</span>}
                    {r.same_format === true && <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-emerald-100 text-emerald-700">Same format</span>}
                    {r.same_format == null && <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-zinc-100 text-zinc-600">Analysis</span>}
                    {r.no_reference && <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-amber-100 text-amber-700">No prior-year reference</span>}
                  </div>
                  <p className="text-sm text-[#18181B] leading-relaxed" data-testid="sm-format-summary">{r.summary}</p>

                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => openDocLink(r.current)} data-testid="sm-format-current-link"
                      className="text-left p-3 rounded-xl border border-[#6043EF]/30 bg-violet-50 hover:bg-violet-100 transition-colors">
                      <div className="text-[9px] font-black uppercase tracking-wider text-[#6043EF]">This document</div>
                      <div className="text-xs font-bold text-[#09090B] mt-0.5">{r.current?.year} · R{r.current?.round}</div>
                      {r.current_format && <div className="text-[10px] text-zinc-500 mt-1 line-clamp-3">{r.current_format}</div>}
                      <div className="text-[10px] text-[#6043EF] font-bold mt-1 inline-flex items-center gap-1"><ArrowSquareOut size={11} weight="bold" /> Open PDF</div>
                    </button>
                    {r.previous ? (
                      <button onClick={() => openDocLink(r.previous)} data-testid="sm-format-previous-link"
                        className="text-left p-3 rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 transition-colors">
                        <div className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Reference (last year)</div>
                        <div className="text-xs font-bold text-[#09090B] mt-0.5">{r.previous?.year} · R{r.previous?.round}</div>
                        {r.previous_format && <div className="text-[10px] text-zinc-500 mt-1 line-clamp-3">{r.previous_format}</div>}
                        <div className="text-[10px] text-[#6043EF] font-bold mt-1 inline-flex items-center gap-1"><ArrowSquareOut size={11} weight="bold" /> Open PDF</div>
                      </button>
                    ) : (
                      <div className="p-3 rounded-xl border border-dashed border-zinc-200 bg-zinc-50 text-[10px] text-zinc-400 flex items-center justify-center">No comparable prior-year document found</div>
                    )}
                  </div>

                  {Array.isArray(r.key_differences) && r.key_differences.length > 0 && (
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-1.5">Key differences</div>
                      <ul className="space-y-1.5" data-testid="sm-format-diffs">
                        {r.key_differences.map((k, i) => (
                          <li key={i} className="text-xs text-[#3F3F46] flex gap-2"><span className="text-amber-500 font-bold">›</span><span>{k}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.likely_cause && (
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                      <div className="text-[10px] font-black uppercase tracking-wider text-amber-700 mb-1">Why the parser found 0 rows</div>
                      <p className="text-xs text-[#78350F] leading-relaxed">{r.likely_cause}</p>
                    </div>
                  )}
                </div>
              ); })()}
            </div>
          </div>
        </div>
      )}
      {!bodiesOpen && (
        <button onClick={() => setBodiesOpen(true)} data-testid="open-bodies-drawer"
          title="Counselling bodies"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-1.5 px-2 py-3 rounded-l-xl bg-[#6043EF] text-white shadow-lg shadow-[#6043EF]/30 hover:bg-[#4f36c9] transition-colors">
          <Buildings size={15} weight="bold" />
          <span className="text-[11px] font-black uppercase tracking-wider [writing-mode:vertical-rl] [text-orientation:upright]">Bodies{activeBodyShort ? ` · ${activeBodyShort}` : ""}</span>
        </button>
      )}
      <BodiesDrawer open={bodiesOpen} onClose={() => setBodiesOpen(false)} summary={bodiesSummary}
        active={filters.body} onPick={(b) => { setF("body", b); setBodiesOpen(false); }} />
    </div>
  );
}


// Per-row misclassification hint — saves silently on blur / Enter.
function HintCell({ url, initial }) {
  const [val, setVal] = useState(initial || "");
  const [state, setState] = useState("idle"); // idle | saving | saved
  const saved = useRef(initial || "");

  const save = async () => {
    if (val === saved.current) return;
    setState("saving");
    try {
      await setCounsellingDocHint(url, val);
      saved.current = val;
      setState("saved");
      setTimeout(() => setState("idle"), 1500);
    } catch (e) { setState("idle"); }
  };

  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="misclassification hint…"
        data-testid={`hint-input-${url}`}
        className="h-6 w-full max-w-[280px] px-1.5 text-[10px] border border-zinc-200 rounded focus:border-[#6043EF] outline-none text-zinc-600 placeholder:text-zinc-300"
      />
      {state === "saving" && <span className="text-[9px] text-zinc-400">…</span>}
      {state === "saved" && <span className="text-[9px] font-bold text-emerald-600" data-testid={`hint-saved-${url}`}>saved</span>}
      {state === "idle" && saved.current && <span className="text-[9px] text-emerald-500" title="hint saved">●</span>}
    </div>
  );
}

// Per-row table-parsing hint (Step 2 HITL) — only shown for NMC seat-matrix
// docs. Saved on blur/Enter, then the "Re-ingest (Step 2)" button in the
// Actions column picks it up automatically on the next run.
function TableHintCell({ url, initial }) {
  const [val, setVal] = useState(initial || "");
  const [state, setState] = useState("idle");
  const saved = useRef(initial || "");

  const save = async () => {
    if (val === saved.current) return;
    setState("saving");
    try {
      await setNmcTableHint(url, val);
      saved.current = val;
      setState("saved");
      setTimeout(() => setState("idle"), 1500);
    } catch (e) { setState("idle"); }
  };

  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="table parsing hint (e.g. skip page 34, ignore consolidated table)"
        data-testid={`table-hint-input-${url}`}
        className="h-6 w-full max-w-[380px] px-1.5 text-[10px] border border-violet-200 rounded focus:border-[#6043EF] outline-none text-zinc-600 placeholder:text-zinc-300 bg-violet-50/30"
      />
      {state === "saving" && <span className="text-[9px] text-zinc-400">…</span>}
      {state === "saved" && <span className="text-[9px] font-bold text-emerald-600" data-testid={`table-hint-saved-${url}`}>saved</span>}
      {state === "idle" && saved.current && <span className="text-[9px] text-emerald-500" title="table hint saved">●</span>}
    </div>
  );
}

